import React, { useState, useEffect, useRef } from "react";
import "./EmailAiPopup.css";
import "./EmailAiModes/BulkModeEmail.css";
import "./EmailAiModes/AutoSendModeEmail.css";
import { ref, onValue, get, set } from 'firebase/database';
import { database } from '../firebaseConfig';
import NormalModeEmail from './EmailAiModes/NormalModeEmail';
import InboxModeEmail from './EmailAiModes/InboxModeEmail';
import BulkModeEmail from './EmailAiModes/BulkModeEmail';
import AutoSendModeEmail from './EmailAiModes/AutoSendModeEmail';
import { standardizeInstagramProfile, isRecentlyAdded } from '../utils/InstagramHelper';

const EmailAiPopup = ({ 
  onClose, 
  rowData, 
  userId, 
  isReply = false, 
  source = "", 
  onEmailResult,
  bulkMode = false,
  autoSendMode = false,
  bulkContacts = [],
  allData = []
}) => {
  const [userSettings, setUserSettings] = useState({});
  const [aiSettings, setAiSettings] = useState({});
  const [userTopics, setUserTopics] = useState({});
  const [isTopicsLoaded, setIsTopicsLoaded] = useState(false);
  const [senderEmails, setSenderEmails] = useState([]);
  const [isAutoSendProcessing, setIsAutoSendProcessing] = useState(false);
  const [whisperSettings, setWhisperSettings] = useState({
    smartAddEnabled: false,
    profileNumber: 0
  });
  const [selectedTopic, setSelectedTopic] = useState(null);
  const hasAutoGenerated = React.useRef(false);

  // Bestimme den Modus basierend auf den Props
  const getMode = () => {
    if (autoSendMode) return "auto";
    if (bulkMode) return "bulk";
    if (isReply || source === "inbox") return "inbox";
    return "normal";
  };

  const mode = getMode();

  // Verhindere Scrollen im Hintergrund, ohne die Scroll-Position zu verlieren
  const scrollPosRef = useRef(0);
  useEffect(() => {
    // Aktuelle Scroll-Position merken
    scrollPosRef.current = window.scrollY || document.documentElement.scrollTop;

    // Body fixieren, um Hintergrund-Scrollen zu verhindern
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollPosRef.current}px`;
    document.body.classList.add('email-ai-popup-open');

    return () => {
      // Styling zurücksetzen
      document.body.classList.remove('email-ai-popup-open');
      document.body.style.position = '';
      document.body.style.top = '';

      // Scroll-Position wiederherstellen
      window.scrollTo(0, scrollPosRef.current);
    };
  }, []);

  // Lade die Benutzereinstellungen
  useEffect(() => {
    if (!userId) return;
    
    // Lade E-Mail-Einstellungen
    const settingsRef = ref(database, `/World/${userId}/settings/email`);
    onValue(settingsRef, (snapshot) => {
      if (snapshot.exists()) {
        setUserSettings(snapshot.val());
      }
    });
    
    // Lade AI-Einstellungen
    const aiSettingsRef = ref(database, `/World/${userId}/settings/ai`);
    onValue(aiSettingsRef, (snapshot) => {
      if (snapshot.exists()) {
        setAiSettings(snapshot.val());
      }
    });

    // Lade Footer-Einstellungen
    const footerSettingsRef = ref(database, `/World/${userId}/settings/footer`);
    onValue(footerSettingsRef, (snapshot) => {
      if (snapshot.exists()) {
        const footerSettings = snapshot.val();
        setUserSettings(prev => ({
          ...prev,
          footer_text: footerSettings.footer_text,
          footer_image_url: footerSettings.footer_image_url,
          footer_image_position: footerSettings.footer_image_position,
          footer_font: footerSettings.footer_font,
          footer_image_size: footerSettings.footer_image_size
        }));
      }
    });

    // Lade Absender-E-Mails
    const senderEmailsRef = ref(database, `/World/${userId}/settings/senderEmails`);
    onValue(senderEmailsRef, (snapshot) => {
      if (snapshot.exists()) {
        const emails = [];
        snapshot.forEach((childSnapshot) => {
          emails.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
        setSenderEmails(emails);
      } else if (userSettings.email_username) {
        // Fallback auf die Einstellungen
        setSenderEmails([{
          id: 'default',
          email: userSettings.email_username,
          password: userSettings.email_password,
          sender_name: userSettings.user_name || '',
          imap_host: userSettings.imap_host || 'imap.hostinger.com',
          imap_port: userSettings.imap_port || '993',
          encryption_type: userSettings.encryption_type || 'SSL',
          smtp_host: userSettings.smtp_host || 'smtp.hostinger.com',
          smtp_port: userSettings.smtp_port || '465',
          smtp_encryption: userSettings.smtp_encryption || 'true'
        }]);
      }
    });

    // Lade Whisper-Einstellungen
    const whisperSettingsRef = ref(database, `/Whisper/${userId}/db/settings`);
    onValue(whisperSettingsRef, (snapshot) => {
      if (snapshot.exists()) {
        const settings = snapshot.val();
        setWhisperSettings({
          smartAddEnabled: settings.smartAddEnabled || false,
          profileNumber: settings.profileNumber || 0
        });
      }
    });

    // Versuche, unvollständige Status-Updates zu finden und zu verarbeiten
    const processIncompleteStatusUpdates = async () => {
      try {
        const statusUpdatesRef = ref(database, `/World/${userId}/statusUpdates`);
        const snapshot = await get(statusUpdatesRef);
        
        if (snapshot.exists()) {
          const updates = snapshot.val();
          
          // Gehe alle Status-Updates durch
          for (const [leadId, updateData] of Object.entries(updates)) {
            // Suche nach Updates mit Status "queued" oder "error", die älter als 5 Minuten sind
            if ((updateData.status === 'queued' || updateData.status === 'error') && 
                updateData.timestamp && 
                Date.now() - updateData.timestamp > 5 * 60 * 1000) {
              
              console.log(`Verarbeite unvollständiges Status-Update für Lead ${leadId}: ${updateData.newStatus}`);
            }
          }
        }
      } catch (error) {
        console.error("Fehler beim Suchen nach unvollständigen Status-Updates:", error);
      }
    };
    
    processIncompleteStatusUpdates();
  }, [userId, userSettings.email_username]);

  // Lade alle Topic-Daten
  useEffect(() => {
    if (!userId) return;
    
    let isMounted = true;
    setIsTopicsLoaded(false);
    
    const loadAllTopicData = async () => {
      try {
        // Referenzen erstellen
        const selectedTopicsRef = ref(database, `/World/${userId}/settings/selectedTopics`);
        const customTopicsRef = ref(database, `/World/${userId}/settings/customTopics`);
        
        // Daten parallel laden
        const [selectedSnapshot, customSnapshot] = await Promise.all([
          new Promise(resolve => onValue(selectedTopicsRef, snapshot => resolve(snapshot), { onlyOnce: true })),
          new Promise(resolve => onValue(customTopicsRef, snapshot => resolve(snapshot), { onlyOnce: true }))
        ]);
        
        if (!isMounted) return;
        
        // Kombiniere alle Topics in einem Objekt
        const allTopics = {};
        
        if (selectedSnapshot.exists()) {
          Object.assign(allTopics, selectedSnapshot.val());
        }
        
        if (customSnapshot.exists()) {
          Object.assign(allTopics, customSnapshot.val());
        }
        
        // Setze den State einmal mit allen Daten
        setUserTopics(allTopics);
        setIsTopicsLoaded(true);
        
      } catch (error) {
        console.error("Fehler beim Laden der Topic-Daten:", error);
        if (isMounted) {
          setIsTopicsLoaded(true); // Trotz Fehler als geladen markieren
        }
      }
    };
    
    // Lade initial alle Daten
    loadAllTopicData();
    
    // Richte Live-Updates ein
    const selectedTopicsRef = ref(database, `/World/${userId}/settings/selectedTopics`);
    const unsubscribe = onValue(selectedTopicsRef, (snapshot) => {
      if (!isMounted) return;
      
      if (snapshot.exists()) {
        // Aktualisiere nur die ausgewählten Topics, behalte benutzerdefinierte bei
        setUserTopics(prev => ({
          ...prev,
          ...snapshot.val()
        }));
      }
    });
    
    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [userId]);

  // Setze das vorausgewählte Topic, wenn vorhanden
  useEffect(() => {
    if (rowData.selectedTopic) {
      setSelectedTopic(rowData.selectedTopic);
      hasAutoGenerated.current = true; // Verhindere automatische Generierung
    }
  }, [rowData.selectedTopic]);

  // Standard Topic-Mapping für alle Modi
  const topicTypeMapping = {
    "Introduce Yourself": "intro",
    "Follow Up": "follow_up_1",
    "Second Follow Up": "follow_up_2",
    "Third Follow Up": "follow_up_3",
    "Reply": "custom_reply"
  };

  // Funktion zum Hinzufügen eines Instagram-Profils zur Whisper-DB
  const addInstagramProfileToWhisper = async (instagramProfile, contact = null) => {
    // Prüfe, ob Smart Send aktiviert ist
    if (!whisperSettings.smartAddEnabled) return;
    
    // Prüfen, ob ein Instagram-Profil vorhanden ist
    if (!instagramProfile) return;
    
    try {
      // Verwende die standardisierte Funktion, um das Instagram-Profil zu normalisieren
      const standardized = standardizeInstagramProfile(instagramProfile);
      
      // Wenn kein gültiger Profilname vorhanden ist, beende die Funktion
      if (!standardized.username) return;
      
      // Prüfe, ob das Profil kürzlich hinzugefügt wurde (zur Vermeidung von Mehrfachhinzufügungen)
      if (isRecentlyAdded(standardized.username)) {
        console.log(`Instagram-Profil ${standardized.username} wurde kürzlich bereits hinzugefügt`);
        return;
      }
      
      // Prüfe, ob das Profil bereits existiert
      const profilesRef = ref(database, `/Whisper/${userId}/db/profiles`);
      const existingProfilesSnapshot = await get(profilesRef);
      
      let profileExists = false;
      
      if (existingProfilesSnapshot.exists()) {
        // Durchlaufe alle Profile und prüfe, ob das Instagram-Profil bereits existiert
        existingProfilesSnapshot.forEach((profileSnapshot) => {
          const profile = profileSnapshot.val();
          if (profile.instagram_profile === standardized.username) {
            profileExists = true;
          }
        });
      }
      
      // Wenn das Profil bereits existiert, beende die Funktion
      if (profileExists) {
        console.log(`Instagram-Profil ${standardized.username} existiert bereits in der Whisper-DB`);
        return;
      }
      
      // Erstelle einen neuen Eintrag in der WhisperDb
      const now = new Date().toISOString();
      const randomId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      
      // Neuer Eintrag für die Whisper-DB
      const newProfileEntry = {
        instagram_profile: standardized.username,
        profile_url: standardized.profile_url,
        status: "fresh",
        import_date: now,
        accountNumber: whisperSettings.profileNumber,
        source: "email_ai",
        raw_value: instagramProfile, // Speichere den ursprünglichen Wert
      };
      
      // Wenn Kontaktdaten vorhanden sind, füge weitere Informationen hinzu
      if (contact) {
        if (contact.lead_name) newProfileEntry.lead_name = contact.lead_name;
        if (contact.lead_email) newProfileEntry.lead_email = contact.lead_email;
        if (contact.lead_phone) newProfileEntry.lead_phone = contact.lead_phone;
        if (contact.lead_business) newProfileEntry.lead_business = contact.lead_business;
      }
      
      // Speichere den Eintrag in der Whisper-DB
      await set(ref(database, `/Whisper/${userId}/db/profiles/${randomId}`), newProfileEntry);
      
      console.log(`Instagram-Profil ${standardized.username} zur Whisper-DB hinzugefügt`);
    } catch (error) {
      console.error("Fehler beim Hinzufügen des Instagram-Profils zur Whisper-DB:", error);
    }
  };

  // Gemeinsame Props für alle Modi
  const commonProps = {
    onClose,
    rowData,
    userId,
    isReply,
    source,
    onEmailResult: async (status, message, contact = null, scheduledTime = null) => {
      // Debug-Info ausgeben
      console.log("EmailAiPopup onEmailResult:", { status, message, scheduledTime, typeOfScheduledTime: typeof scheduledTime });
      
      // Rufe die ursprüngliche onEmailResult-Funktion auf
      if (onEmailResult) {
        await onEmailResult(status, message, contact, scheduledTime);
      }
      
      // Füge das Instagram-Profil zur Whisper-DB hinzu, wenn vorhanden
      const instagramProfile = contact?.lead_instagram || rowData?.lead_instagram;
      if (instagramProfile) {
        await addInstagramProfileToWhisper(instagramProfile, contact || rowData);
      }
    },
    userSettings,
    aiSettings,
    userTopics,
    isTopicsLoaded,
    senderEmails,
    topicTypeMapping
  };

  // Rückverfolgung während des Ladens
  if (!isTopicsLoaded) {
    return (
      <div className="emailAiPopupOverlay">
        <div className="emailAiPopupContainer loading">
          <div className="emailAiPopupHeader">
            <h2>E-Mail wird geladen...</h2>
            <button className="emailAiPopupCloseButton" onClick={onClose}>×</button>
          </div>
          <div className="emailAiPopupContent">
            <div className="emailAiPopupLoadingSpinner"></div>
          </div>
        </div>
      </div>
    );
  }

  // Verschiedene Modi rendern
  const renderModeComponent = () => {
    switch (mode) {
      case "auto":
        return (
          <AutoSendModeEmail 
            {...commonProps}
            bulkContacts={bulkContacts}
            allData={allData}
            onProcessingChange={setIsAutoSendProcessing}
          />
        );
      case "bulk":
        return (
          <BulkModeEmail 
            {...commonProps}
            bulkContacts={bulkContacts}
            allData={allData}
          />
        );
      case "inbox":
        return (
          <InboxModeEmail 
            {...commonProps} 
          />
        );
      default:
        return (
          <NormalModeEmail 
            {...commonProps} 
          />
        );
    }
  };

  // Sicherer onClose-Handler
  const handleClose = () => {
    // Verhindere das Schließen während der AutoSend-Verarbeitung
    if (mode === "auto" && isAutoSendProcessing) {
      console.log("Schließen während der AutoSend-Verarbeitung verhindert");
      return;
    }
    
    // Normaler onClose-Aufruf
    onClose();
  };

  // Bestimme die CSS-Klassen basierend auf dem Modus
  const getOverlayClasses = () => {
    const baseClass = "emailAiPopupOverlay";
    return `${baseClass} fullWidth`;
  };

  return (
    <div className={getOverlayClasses()}>
      {/* Globaler Close-Button, immer sichtbar */}
      <button 
        className="emailAiPopupCloseButton emailAiPopupGlobalCloseButton" 
        onClick={handleClose}
        disabled={mode === "auto" && isAutoSendProcessing}
        style={mode === "auto" && isAutoSendProcessing ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
      >
        {mode === "auto" && isAutoSendProcessing ? 
          <span title="Cannot close during processing">×</span> : 
          '×'
        }
      </button>
      
      {/* EmailAiPopup-Content */}
      {renderModeComponent()}
    </div>
  );
};

export default EmailAiPopup;
    

